import React, { useEffect, useState } from 'react';
import { Container, Row, Card, Col, Table, Badge, Button } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import BreadCrumb from '../../../components/common/BreadCrumb';
import { getBitcoinPredictions, getBitcoinInitialValue } from '../../../slices/veloxity/bitcoin/BitcoinPredictionReducer';
import Loader from '../../../components/common/Loader';
import buySound from '../../../assets/sounds/Buy.mp3';
import sellSound from '../../../assets/sounds/Sell.mp3';
import holdSound from '../../../assets/sounds/Hold.mp3';

const TimeCounter = ({ startDate, textColor = "text-dark" }) => {
  const [timeElapsed, setTimeElapsed] = useState('');

  useEffect(() => {
    const updateTimer = () => {
      const now = new Date();
      const start = new Date(startDate);
      
      const diff = now - start - (3 * 60 * 60 * 1000);

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      const timeString = hours > 0 
        ? `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        : `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

      setTimeElapsed(timeString);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [startDate]);

  return (
    <div className="d-flex align-items-center">
      <span className={`fw-bold ${textColor}`}>
        {timeElapsed}
      </span>
    </div>
  );
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  return `${date.getDate()}-${months[date.getMonth()]}-${date.getFullYear()} ${date.toLocaleTimeString()}`;
};

const getOrderTypeDisplay = (position, changePosition) => {
  switch(changePosition) {
    case 'Change':
      return position === 'Long' ? 'Buy' : 'Sell';
    case 'No Change':
      return 'Hold';
    case 'No-Change':
      return 'Hold';
    case 'Initial':
      return position === 'Long' ? 'Buy' : 'Sell';
    case '':
      return 'Hold';
    default:
      return position;
  }
};

const getOrderTypeBadgeColor = (position, changePosition) => {
  switch(changePosition) {
    case 'Change':
      return position === 'Long' ? 'success' : 'danger';
    case 'No Change':
      return 'secondary';
    case 'No-Change':
      return 'secondary';
    case 'Initial':
      return position === 'Long' ? 'success' : 'danger';
    case '':
      return 'secondary';
    default:
      return position === 'Long' ? 'success' : 'danger';
  }
};

const getOrderStatusColor = (changePosition) => {
  switch(changePosition) {
    case 'Change':
      return 'warning';
    case 'No Change':
    case 'No-Change':
      return 'info';
    case 'Initial':
      return 'primary';
    case '':
      return 'info';
    default:
      return 'secondary';
  }
};

const LastOrderCard = ({ lastOrder, getOrderTypeColor, audioEnabled }) => {
  const [isNewOrder, setIsNewOrder] = useState(false);
  const [prevOrderId, setPrevOrderId] = useState(null);

  const { initialValue } = useSelector((state) => {
    return state.bitcoinPrediction;
  });

  const getSound = (position, changePosition) => {
    if (changePosition === "Change") {
      return position === "Long" ? buySound : sellSound;
    }
    return holdSound;
  };

  useEffect(() => {
    if (lastOrder?.id) {
      setPrevOrderId(lastOrder.id);
    }
  }, [lastOrder?.id]);

  useEffect(() => {
    if (lastOrder?.id && lastOrder.id !== prevOrderId && audioEnabled) {
      const soundToPlay = getSound(lastOrder.position, lastOrder.change_position);
      
      const audio = new Audio(soundToPlay);
      audio.volume = 0.5;
      
      audio.play().catch(error => {
        console.error("Audio playback failed:", error);
      });

      setIsNewOrder(true);
    }
  }, [lastOrder?.id, prevOrderId, audioEnabled]);

  useEffect(() => {
    let timer;
    if (isNewOrder) {
      timer = setTimeout(() => {
        setIsNewOrder(false);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [isNewOrder]);

  if (!lastOrder) return null;

  return (
    <Card 
      className={`mb-4 ${isNewOrder ? 'highlight-animation' : ''}`} 
      style={{ 
        background: 'linear-gradient(to right, #1a237e, #0d47a1)',
        transition: 'all 0.3s ease-in-out'
      }}
    >
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            <div className="position-relative me-3">
              <div className="pulse-ring"></div>
              <i className="ri-exchange-funds-fill text-warning fs-2"></i>
            </div>
            <h5 className="card-title mb-0 text-white fw-bold fs-2">LAST ORDER</h5>
          </div>
          {initialValue && (
            <div className="d-flex align-items-center">
              <div>
                <div className="text-white-50 me-2">Initial Value</div>
                <div className="text-white fw-bold fs-4">
                  ${Number(initialValue?.initial_price || 0).toLocaleString()}
                </div>
                <small className="text-white-50" style={{ fontSize: '11px' }}>
                  Start Date: {formatDate(initialValue?.create_date)}
                </small>
              </div>
            </div>
          )}
        </div>
        
        <div className="row g-0 mt-4">
          <div className="col-md-3 border-end border-light-subtle">
            <div className="px-3">
              <div className="text-white-50 small fs-5 fw-bold d-flex align-items-center">
                Order Time
                <span className="ms-2 badge bg-light bg-opacity-25 text-white small">UTC</span>
              </div>
              <div className="text-white mt-1 fs-5">
                {formatDate(lastOrder.prediction_end)}
              </div>
            </div>
          </div>
          <div className="col-md-3 border-end border-light-subtle">
            <div className="px-3">
              <div className="text-white-50 small fs-5 fw-bold">Order Status</div>
              <div className="mt-1">
                <Badge color={getOrderStatusColor(lastOrder.change_position)} className="fs-5 px-3 py-1"
                  style={{ minWidth: '80px', textAlign: 'center' }}>
                  {lastOrder.change_position === "" ? "No Change" : lastOrder.change_position}
                </Badge>
              </div>
            </div>
          </div>
          <div className="col-md-3 border-end border-light-subtle">
            <div className="px-3">
              <div className="text-white-50 small fs-5 fw-bold">Order Type</div>
              <div className="mt-1">
                <Badge 
                  color={getOrderTypeBadgeColor(lastOrder.position, lastOrder.change_position)}
                  className="fs-5 px-3 py-1"
                  style={{ minWidth: '80px', textAlign: 'center' }}
                >
                  {getOrderTypeDisplay(lastOrder.position, lastOrder.change_position)}
                </Badge>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="px-3">
              <div className="text-white-50 small fs-5 fw-bold">Elapsed Time Since Last Order</div>
              <div className="mt-1">
                <TimeCounter startDate={lastOrder.create_date} textColor="text-white fs-5" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

const BitcoinAnalytics = () => {
  const dispatch = useDispatch();
  const { loading, payload, error, lastUpdated } = useSelector((state) => state.bitcoinPrediction);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  // localStorage'dan ses ayarını al, yoksa false olarak başlat
  const [audioEnabled, setAudioEnabled] = useState(() => {
    const savedAudio = localStorage.getItem('bitcoinAudioEnabled');
    return savedAudio ? JSON.parse(savedAudio) : false;
  });

  const handleRefresh = () => {
    dispatch(getBitcoinPredictions());
  };

  const toggleSound = () => {
    const newAudioState = !audioEnabled;
    setAudioEnabled(newAudioState);
    localStorage.setItem('bitcoinAudioEnabled', JSON.stringify(newAudioState));
    
    // Test sesi olarak Hold sesini kullanalım
    if (newAudioState) {
      const audio = new Audio(holdSound);
      audio.volume = 0.5;
      audio.play().catch(error => {
        console.error("Audio test failed:", error);
      });
    }
  };

  const getOrderTypeColor = (type) => {
    switch(type) {
      case 'Initial': return 'info';
      case 'Change': return 'warning';
      case 'No-Change': return 'primary';
      default: return 'light';
    }
  };

  useEffect(() => {
    dispatch(getBitcoinInitialValue());
    dispatch(getBitcoinPredictions());

    const interval = setInterval(() => {
      dispatch(getBitcoinPredictions());
    }, 10000);

    return () => clearInterval(interval);
  }, [dispatch]);

  const lastOrder = payload?.data ? payload.data[0] : (Array.isArray(payload) ? payload[0] : null);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = payload?.data 
    ? payload.data.slice(indexOfFirstItem, indexOfLastItem) 
    : [];
  const totalRecords = payload?.totalRecords || 0;
  const totalPages = Math.ceil(totalRecords / itemsPerPage);
  const hasMoreData = useSelector(state => state.bitcoinPrediction.hasMoreData);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  document.title = "AIMEE - Bitcoin Analytics";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Bitcoin Analytics" pageTitle="Bitcoin" />
          <Row>
            <Card
              className="p-3"
              style={{
                boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                marginBottom: "30px",
                marginLeft: "1%",
                width: "98%",
              }}
            >
              <Row>
                <Col lg={12}>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="d-flex align-items-center gap-2">
                      <h4 className="mb-0">Bitcoin Predictions</h4>
                      <Button 
                        color="light" 
                        size="sm" 
                        onClick={handleRefresh}
                        disabled={loading}
                      >
                        <i className="ri-refresh-line align-bottom me-1"></i>
                        Refresh
                      </Button>
                      <Button
                        color={audioEnabled ? "success" : "light"}
                        size="sm"
                        onClick={toggleSound}
                      >
                        <i className={`ri-${audioEnabled ? 'volume-up' : 'volume-mute'}-line align-bottom me-1`}></i>
                        {audioEnabled ? 'Sound On' : 'Sound Off'}
                      </Button>
                    </div>
                    {lastUpdated && (
                      <small className="text-muted">
                        Last updated: {new Date(lastUpdated).toLocaleTimeString()}
                      </small>
                    )}
                  </div>

                  <LastOrderCard 
                    lastOrder={lastOrder} 
                    getOrderTypeColor={getOrderTypeColor}
                    audioEnabled={audioEnabled}
                  />
                  
                  {loading && <Loader />}
                  
                  {error && (
                    <div className="alert alert-danger">
                      Error loading Bitcoin predictions: {error}
                    </div>
                  )}

                  {(payload?.data || payload) && (
                    <>
                      <div className="table-responsive">
                        <Table className="table-striped table-nowrap align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th className="fw-semibold">ID</th>
                              <th className="fw-semibold">
                                Order Time
                                <span className="ms-1 badge bg-secondary bg-opacity-25 text-secondary small">UTC</span>
                              </th>
                              <th className="fw-semibold">BTC Price</th>
                              <th className="fw-semibold">Order Status</th>
                              <th className="fw-semibold">Order Type</th>
                              <th className="fw-semibold">Position</th>
                              <th className="fw-semibold">Cash/BTC</th>
                              <th className="fw-semibold text-end">Cash Equivalent</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentItems.map((item) => (
                              <tr key={item.id}>
                                <td>{item.id}</td>
                                <td>
                                  <small className="d-block text-muted">
                                    {formatDate(item.prediction_end)}
                                  </small>
                                </td>
                                <td>
                                  <div className="fw-semibold">
                                    ${item.btc_price.toLocaleString()}
                                  </div>
                                </td>
                                <td>
                                  <Badge color={getOrderStatusColor(item.change_position)}>
                                    {item.change_position || 'No Change'}
                                  </Badge>
                                </td>
                                <td>
                                  <Badge color={getOrderTypeBadgeColor(item.position, item.change_position)}>
                                    {getOrderTypeDisplay(item.position, item.change_position)}
                                  </Badge>
                                </td>
                                <td>
                                  <Badge color={item.position === "Long" ? "success" : "danger"}>
                                    {item.position === "Short" ? "Cash" : item.position}
                                  </Badge>
                                </td>
                                <td>
                                  <div>BTC: {item.btc}</div>
                                  <div>Cash: ${item.cash.toLocaleString()}</div>
                                </td>
                                <td className="text-end fw-semibold">
                                  ${item.total_cash_out.toLocaleString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>

                      <div className="d-flex justify-content-end mt-3">
                        <ul className="pagination">
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => paginate(currentPage - 1)}
                              disabled={currentPage === 1}
                            >
                              Previous
                            </button>
                          </li>
                          {[...Array(totalPages)].map((_, index) => (
                            <li
                              key={index + 1}
                              className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => paginate(index + 1)}
                              >
                                {index + 1}
                              </button>
                            </li>
                          ))}
                          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => paginate(currentPage + 1)}
                              disabled={currentPage === totalPages}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </div>
                      
                      <div className="text-muted mt-2 text-end">
                        Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, totalRecords)} of {totalRecords} entries
                        {hasMoreData && " (Showing last 200 records)"}
                      </div>
                    </>
                  )}
                </Col>
              </Row>
            </Card>
          </Row>
        </Container>
      </div>

      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.7; }
            100% { opacity: 1; }
          }

          .pulse-ring {
            position: absolute;
            width: 30px;
            height: 30px;
            border-radius: 50%;
            background-color: rgba(255, 193, 7, 0.2);
            animation: pulse-ring 1.25s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
          }

          @keyframes pulse-ring {
            0% {
              transform: scale(0.33);
              opacity: 0.8;
            }
            80%, 100% {
              transform: scale(1);
              opacity: 0;
            }
          }

          @keyframes highlight {
            0% {
              background: linear-gradient(to right, #1a237e, #0d47a1);
              transform: translateY(0);
            }
            25% {
              background: linear-gradient(to right, #1b5e20, #2e7d32);
              transform: translateY(-3px);
              box-shadow: 0 0 30px rgba(46, 125, 50, 0.8);
            }
            50% {
              background: linear-gradient(to right, #1a237e, #0d47a1);
              transform: translateY(0);
            }
            75% {
              background: linear-gradient(to right, #1b5e20, #2e7d32);
              transform: translateY(-3px);
              box-shadow: 0 0 30px rgba(46, 125, 50, 0.8);
            }
            100% {
              background: linear-gradient(to right, #1a237e, #0d47a1);
              transform: translateY(0);
            }
          }

          .highlight-animation {
            animation: highlight 5s ease-in-out;
            animation-iteration-count: 2;
          }

          .highlight-animation .card-body {
            position: relative;
          }

          .highlight-animation .card-body::after {
            content: 'NEW ORDER';
            position: absolute;
            top: -10px;
            right: -10px;
            background-color: #4caf50;
            color: white;
            padding: 5px 10px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: bold;
            animation: blink 1s ease-in-out infinite;
          }

          @keyframes blink {
            0% { opacity: 1; }
            50% { opacity: 0.3; }
            100% { opacity: 1; }
          }
        `}
      </style>
    </React.Fragment>
  );
};

export default BitcoinAnalytics; 